/**
 * Auth Context
 * 
 * Provides authentication state throughout the app.
 * Supports 3 user states:
 * - Anonymous (not signed in)
 * - Visitor (signed in, no church profile)
 * - Member/Admin (signed in, has church profile)
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { trackEvent, identifyUser, groupOrganization, resetAnalytics, EVENTS } from '../utils/analytics';
import { setSentryUser, resetSentryContext } from '../utils/observability';
import { supabase } from '@/lib/supabase';

const AuthContext = createContext(null);

// Get API URL from environment variables
// Get API URL from environment variables, preferring same-origin Vite proxy on localhost
const API_URL = (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
    ? ''
    : (import.meta.env.VITE_API_URL || 'http://localhost:3001');
const AUTH_REQUEST_TIMEOUT_MS = 10000;
const withAuthTimeout = (promise, label) => {
    let timer;
    return Promise.race([
        promise,
        new Promise((_, reject) => {
            timer = setTimeout(() => reject(new Error(`${label} timed out`)), AUTH_REQUEST_TIMEOUT_MS);
        }),
    ]).finally(() => clearTimeout(timer));
};

const fetchWithAuthTimeout = async (url, options) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), AUTH_REQUEST_TIMEOUT_MS);
    try {
        return await fetch(url, { ...options, signal: controller.signal });
    } finally {
        clearTimeout(timer);
    }
};
const hasPostHogConfig = Boolean(
    import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN && import.meta.env.VITE_PUBLIC_POSTHOG_HOST
);

const applyObservabilityIdentity = (authUser, userProfile = null) => {
    if (!authUser?.id) return;
    identifyUser(authUser.id, authUser.email, userProfile?.role || 'visitor', userProfile?.plan || 'free', userProfile?.church_id || null);
    setSentryUser(authUser.id, authUser.email);
    if (userProfile?.church_id) {
        groupOrganization(userProfile.church_id, userProfile.church_name || 'Unknown', userProfile.plan || 'free');
    }
};

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [session, setSession] = useState(null);
    const [profile, setProfile] = useState(null);
    const [access, setAccess] = useState({ translate: false, live: false, events: false, pastor_intelligence: false });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Load profile from backend after auth
    const loadProfile = useCallback(async (accessToken, forceRefresh = false) => {
        try {
            const url = forceRefresh ? `${API_URL}/api/me?refresh=true` : `${API_URL}/api/me`;
            const response = await fetchWithAuthTimeout(url, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    ...(forceRefresh ? { 'Cache-Control': 'no-cache' } : {})
                },
            });

            if (response.ok) {
                const data = await response.json();
                // Profile can be null for visitors - that's OK!
                setProfile(data.profile);
                setError(null);
                
                if (data.profile?.church_id) {
                    try {
                        const accessRes = await fetchWithAuthTimeout(`${API_URL}/api/my-access`, {
                            headers: { 'Authorization': `Bearer ${accessToken}` }
                        });
                        if (accessRes.ok) {
                            const accessData = await accessRes.json();
                            setAccess({
                                translate: !!accessData.access?.hasTranslateAccess,
                                live: !!accessData.access?.hasLiveAccess,
                                events: !!accessData.access?.hasEventsAccess,
                                pastor_intelligence: !!accessData.access?.hasPastorIntelligence
                            });
                        } else {
                            setError('api_error');
                        }
                    } catch (e) {
                        console.error('[Auth] Failed to load access:', e);
                        setError('network_error');
                    }
                } else {
                    setAccess({ translate: false, live: false, events: false, pastor_intelligence: false });
                }
            } else if (response.status === 401) {
                // Token invalid - force sign out to recover from stuck state
                console.warn('[Auth] Token rejected by backend (401), signing out...');
                try {
                    await supabase.auth.signOut();
                } catch (e) {
                    console.error('[Auth] Supabase signOut failed during token rejection:', e);
                }
                setUser(null);
                setSession(null);
                setProfile(null);
                setAccess({ translate: false, live: false, events: false, pastor_intelligence: false });
                setError('auth_invalid');
            } else {
                setProfile(null);
                setAccess({ translate: false, live: false, events: false, pastor_intelligence: false });
                setError('api_error');
            }
        } catch (err) {
            console.error('[Auth] Failed to load profile:', err);
            setProfile(null);
            setAccess({ translate: false, live: false, events: false, pastor_intelligence: false });
            setError('network_error');
        }
    }, []);

    // Initialize auth state
    useEffect(() => {
        let subscription;
        let mounted = true;

        const initAuth = async () => {
            try {
                // Get initial session
                const { data: { session: initialSession }, error: sessionError } = await withAuthTimeout(supabase.auth.getSession(), 'Session retrieval');

                if (sessionError) {
                    console.warn('[Auth] Session error:', sessionError.message);
                    // Clear any stale session
                    try {
                        await supabase.auth.signOut();
                    } catch (e) {
                        console.error('[Auth] Supabase signOut failed during session error:', e);
                    }
                    if (mounted) setLoading(false);
                    return;
                }

                if (initialSession) {
                    // Check if token is about to expire (within 60 seconds)
                    const expiresAt = initialSession.expires_at;
                    const now = Math.floor(Date.now() / 1000);
                    const isExpiringSoon = expiresAt && (expiresAt - now) < 60;

                    if (isExpiringSoon) {
                        console.log('[Auth] Token expiring soon, refreshing...');
                        const { data: { session: refreshedSession }, error: refreshError } = await withAuthTimeout(supabase.auth.refreshSession(), 'Session refresh');

                        if (refreshError || !refreshedSession) {
                            console.warn('[Auth] Token refresh failed:', refreshError?.message);
                            // Token is truly expired, sign out
                            try {
                                await supabase.auth.signOut();
                            } catch (e) {
                                console.error('[Auth] Supabase signOut failed during refresh failure:', e);
                            }
                            if (mounted) setLoading(false);
                            return;
                        }

                        if (mounted) {
                            setSession(refreshedSession);
                            setUser(refreshedSession.user);
                            applyObservabilityIdentity(refreshedSession.user, profile);
                        }
                        await loadProfile(refreshedSession.access_token);
                    } else {
                        if (mounted) {
                            setSession(initialSession);
                            setUser(initialSession.user);
                            applyObservabilityIdentity(initialSession.user, profile);
                        }
                        await loadProfile(initialSession.access_token);
                    }
                }
            } catch (err) {
                console.error('[Auth] Init error:', err);
                if (mounted) setError('network_error');
            }
            
            if (!mounted) return;
            setLoading(false);

            // Listen for auth changes AFTER initialization is complete to avoid Web Lock collisions
            const { data } = supabase.auth.onAuthStateChange(
                async (event, session) => {
                    console.log('[Auth] State changed:', event);

                    if (event === 'TOKEN_REFRESHED') {
                        console.log('[Auth] Token refreshed automatically');
                    }

                    if (!mounted) return;

                    setSession(session);
                    setUser(session?.user ?? null);

                    if (session?.user) {
                        applyObservabilityIdentity(session.user, profile);

                        const authMethod = session.user.app_metadata?.provider || 'email';
                        if (event === 'SIGNED_IN' && authMethod !== 'email' && hasPostHogConfig) {
                            trackEvent(EVENTS.LOGIN_COMPLETED, {
                                auth_method: authMethod,
                            });
                        }
                    }

                    if (session?.access_token) {
                        await loadProfile(session.access_token);
                    } else {
                        setProfile(null);
                        setAccess({ translate: false, live: false, events: false, pastor_intelligence: false });
                    }
                }
            );
            subscription = data.subscription;
        };

        initAuth();

        return () => {
            mounted = false;
            if (subscription) subscription.unsubscribe();
        };
    }, [loadProfile]);


    // Force reload profile (after joining a church)
    const reloadProfile = async () => {
        if (session?.access_token) {
            setLoading(true);
            try {
                await loadProfile(session.access_token, true);
            } finally {
                setLoading(false);
            }
        }
    };

    // Sign up with email (requires email confirmation)
    // redirectAfter: optional URL path to redirect to after email verification
    const signUpWithEmail = async (email, password, redirectAfter) => {
        // Don't set global loading here, let the component handle its own loading state
        // otherwise App.jsx unmounts the page because it listens to global loading
        setError(null);

        try {
            console.log('[AuthContext] Signing up with email:', email);
            // Build emailRedirectTo with optional redirect param
            let emailRedirectTo = `${window.location.origin}/signup`;
            if (redirectAfter) {
                emailRedirectTo += `?redirect=${encodeURIComponent(redirectAfter)}`;
            }

            const { data, error } = await supabase.auth.signUp({
                email,
                password,
                options: {
                    emailRedirectTo,
                },
            });

            console.log('[AuthContext] Sign up response:', { data, error });

            if (error) {
                console.error('[AuthContext] Sign up error:', error);
                setError(error.message);
                return { error };
            }

            console.log('[AuthContext] Sign up successful, user:', data.user?.id);
            applyObservabilityIdentity(session.user, profile);
            if (hasPostHogConfig) {
                trackEvent(EVENTS.SIGNUP_COMPLETED, {
                    auth_method: 'email',
                    email_confirmation_required: !data.session,
                });
            }
            return { data };
        } catch (err) {
            console.error('[AuthContext] Unexpected sign up error:', err);
            setError(err.message || 'An unexpected error occurred');
            return { error: err };
        }
    };

    // Resend confirmation email
    const resendConfirmationEmail = async (email, redirectAfter) => {
        try {
            console.log('[AuthContext] Resending confirmation email for:', email);
            let emailRedirectTo = `${window.location.origin}/signup`;
            if (redirectAfter) {
                emailRedirectTo += `?redirect=${encodeURIComponent(redirectAfter)}`;
            }

            const { data, error } = await supabase.auth.resend({
                type: 'signup',
                email,
                options: {
                    emailRedirectTo,
                },
            });

            if (error) {
                console.error('[AuthContext] Resend error:', error);
                return { error };
            }

            return { data };
        } catch (err) {
            console.error('[AuthContext] Unexpected resend error:', err);
            return { error: err };
        }
    };

    // Sign in with email
    const signInWithEmail = async (email, password) => {
        setLoading(true);
        setError(null);

        try {
            const { data, error } = await supabase.auth.signInWithPassword({
                email,
                password,
            });

            if (error) {
                setError(error.message);
                setLoading(false);
                return { error };
            }

            applyObservabilityIdentity(session.user, profile);
            if (hasPostHogConfig) {
                trackEvent(EVENTS.LOGIN_COMPLETED, {
                    auth_method: 'email',
                });
            }

            setLoading(false);
            return { data };
        } catch (err) {
            console.error('[AuthContext] Login error:', err);
            
            if (err.name === 'AbortError') {
                setError('Login request was interrupted. Please try logging in again.');
            } else {
                setError(err.message || 'An unexpected error occurred during login');
            }
            
            setLoading(false);
            return { error: err };
        }
    };

    // Sign in with Google OAuth
    // redirectAfter: optional URL path to redirect to after OAuth callback
    const signInWithGoogle = async (redirectAfter) => {
        // Build redirectTo with optional redirect param
        let redirectTo = `${window.location.origin}/signin`;
        if (redirectAfter) {
            redirectTo += `?redirect=${encodeURIComponent(redirectAfter)}`;
        }

        try {
            const { data, error } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {
                    redirectTo,
                },
            });

            if (error) {
                setError(error.message);
                return { error };
            }

            return { data };
        } catch (err) {
            console.error('[AuthContext] Google login error:', err);
            setError(err.message || 'An unexpected error occurred during Google login');
            return { error: err };
        }
    };

    // Reset password (sends email)
    const resetPassword = async (email) => {
        try {
            console.log('[AuthContext] Resetting password for:', email);
            const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: `${window.location.origin}/`,
            });
            if (error) return { error };
            return { data };
        } catch (err) {
            return { error: err };
        }
    };

    // Update password (for logged in user)
    const updatePassword = async (newPassword) => {
        try {
            console.log('[AuthContext] Updating user password');
            
            if (!session) {
                console.error('[AuthContext] Update password failed: No session present');
                return { error: new Error('Your session has expired. Please sign in again.') };
            }

            const { data, error } = await supabase.auth.updateUser({
                password: newPassword
            });
            
            if (error) {
                console.error('[AuthContext] Supabase update error:', error.message);
                return { error };
            }
            
            return { data };
        } catch (err) {
            console.error('[AuthContext] Unexpected update error:', err);
            return { error: err };
        }
    };

    // Sign out
    const signOut = async () => {
        const wasAuthenticated = Boolean(session);
        setLoading(true);
        try {
            await supabase.auth.signOut();
        } catch (e) {
            console.error('[AuthContext] Supabase signOut error:', e);
        }
        if (wasAuthenticated) { resetAnalytics(); resetSentryContext(); }
        setUser(null);
        setSession(null);
        setProfile(null);
        setAccess({ translate: false, live: false, events: false, pastor_intelligence: false });
        setError(null);
        
        // Clear all session storage and live onboarding to prevent cross-account/session leakage
        if (typeof window !== 'undefined') {
            try {
                localStorage.removeItem('exbabel-live-onboarding-store');
                window.dispatchEvent(new CustomEvent('exbabel:signout'));
            } catch (e) {}
        }
        sessionStorage.removeItem('exbabel_host_session');
        sessionStorage.removeItem('exbabel_host_transcript');
        sessionStorage.removeItem('exbabel_autostart_broadcast');
        sessionStorage.removeItem('exbabel_autostart_redirect');
        
        const keysToRemove = [];
        for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key && key.startsWith('exbabel_listener_history_')) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach(k => sessionStorage.removeItem(k));
        
        setLoading(false);
    };

    // Get current access token
    const getAccessToken = () => session?.access_token;

    // Computed states
    const isAuthenticated = !!user;
    const isVisitor = isAuthenticated && !profile && !error;
    const isMember = isAuthenticated && profile && profile.role === 'member';
    const isAdmin = isAuthenticated && profile?.role === 'admin';
    const hasChurch = !!profile?.church_id;

    const value = {
        user,
        session,
        profile,
        access,
        loading,
        error,
        // Auth methods
        signUpWithEmail,
        resendConfirmationEmail,
        signInWithEmail,
        signInWithGoogle,
        resetPassword,
        updatePassword,
        signOut,
        getAccessToken,
        reloadProfile,
        // Computed states
        isAuthenticated,
        isVisitor,
        isMember,
        isAdmin,
        hasChurch,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
